/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { range, zip } from 'lodash';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  layerActions,
  layerSelectors,
  layerTypes,
  mapSelectors,
} from '@opengeoweb/store';
import { LayerType, webmapUtils } from '@opengeoweb/webmap';
import LayerManagerMenuButton from './MenuButton';

interface LayerManagerButtonProps {
  mapId: string;
  layerId?: string;
  tooltipTitle?: string;
  icon?: React.ReactNode;
}

interface AddLayerFromCopyParams {
  mapId: string;
  oldLayerId: string;
  newLayerId: string;
}

const LayerManagerMenuButtonConnect: React.FC<LayerManagerButtonProps> = ({
  layerId,
  mapId,
  tooltipTitle,
  icon,
}: LayerManagerButtonProps) => {
  const dispatch = useDispatch();

  const layerType = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerType(store, layerId),
  );

  const isFeatureLayer = layerType === LayerType.featureLayer;

  // get all map ids
  const allMapIds = useSelector(mapSelectors.getAllMapIds);
  const allOtherMapIdsExceptCurrentOne = allMapIds.filter((id) => id !== mapId);
  const allLayers = useSelector(layerSelectors.getAllLayers);

  const copyLayerToOtherMaps = React.useCallback(() => {
    // generate new layer ids
    const newLayerIds = range(allOtherMapIdsExceptCurrentOne.length).map((_) =>
      webmapUtils.generateLayerId(),
    );
    // run all duplicateLayer calls in a loop
    zip(allOtherMapIdsExceptCurrentOne, newLayerIds).forEach(
      ([newMapId, newLayerId]) => {
        if (!newMapId || !layerId || !newLayerId) {
          return;
        }
        dispatch(
          layerActions.duplicateMapLayer({
            mapId: newMapId,
            oldLayerId: layerId,
            newLayerId,
            origin: layerTypes.LayerActionOrigin.layerManager,
          }),
        );
      },
    );
  }, [allOtherMapIdsExceptCurrentOne, dispatch, layerId]);

  const removeLayerFromAllMaps = React.useCallback(() => {
    if (!layerId) {
      return;
    }
    const layerName = allLayers.find((layer) => layer.id === layerId)?.name;
    layerName &&
      allLayers
        .filter((layer) => layer.name === layerName && layer.mapId !== mapId)
        .forEach((layer) => {
          layer.mapId &&
            layer.id &&
            dispatch(
              layerActions.layerDelete({
                mapId: layer.mapId,
                layerId: layer.id,
              }),
            );
        });
  }, [dispatch, allLayers, mapId, layerId]);

  const duplicateLayer = React.useCallback(
    ({ newLayerId, mapId }: AddLayerFromCopyParams) =>
      dispatch(
        layerActions.duplicateMapLayer({
          mapId,
          oldLayerId: layerId!,
          newLayerId,
          origin: layerTypes.LayerActionOrigin.layerManager,
        }),
      ),
    [dispatch, layerId],
  );

  return (
    <LayerManagerMenuButton
      icon={icon}
      isFeatureLayer={isFeatureLayer}
      layerId={layerId}
      mapId={mapId}
      onLayerCopyToOtherMaps={copyLayerToOtherMaps}
      onLayerDuplicate={(): void => {
        duplicateLayer({
          newLayerId: webmapUtils.generateLayerId(),
          mapId,
        } as AddLayerFromCopyParams);
      }}
      onLayerRemoveFromOtherMaps={removeLayerFromAllMaps}
      tooltipTitle={tooltipTitle}
    />
  );
};

export default LayerManagerMenuButtonConnect;
